import React from "react";
import { Alert, AlertColor, AlertTitle, SxProps } from "@mui/material";
import { Theme } from "@mui/material/styles";

import { Tone } from "./statusLabels";

export interface CalloutProps {
  /** Default 'info'. */
  tone?: Tone;
  /** Bold first line in the tone colour. */
  title?: React.ReactNode;
  /** Body text. */
  children?: React.ReactNode;
  /** Replace the tone icon, or `false` for none. */
  icon?: React.ReactNode | false;
  /** Right-aligned action, e.g. a text Button. */
  action?: React.ReactNode;
  /** 'alert' for errors the person must notice now; default 'status'. */
  role?: "alert" | "status" | "note";
  sx?: SxProps<Theme>;
}

const severityFor: Record<Tone, AlertColor> = {
  ok: "success",
  info: "info",
  warn: "warning",
  err: "error",
  neutral: "info",
};

/**
 * Inline message box (tone background, 4px left rule, tone title), a thin wrapper over
 * MUI Alert whose look comes from the theme.
 *
 *   <Callout tone="warn" title="3 payments didn't go through">
 *     Fix the payee in Tipalti or QuickBooks, then resend.
 *   </Callout>
 */
export function Callout({
  tone = "info",
  title,
  children,
  icon,
  action,
  role = "status",
  sx,
}: CalloutProps) {
  const neutralSx =
    tone === "neutral"
      ? (theme: Theme) => {
          const s = theme.palette.status.neutral;
          return {
            backgroundColor: s.bg,
            borderColor: s.line,
            borderLeftColor: s.fg,
            "& .MuiAlert-icon, & .MuiAlertTitle-root": { color: s.fg },
          };
        }
      : undefined;

  return (
    <Alert
      severity={severityFor[tone]}
      variant="standard"
      icon={icon}
      action={action}
      role={role}
      sx={[
        ...(neutralSx ? [neutralSx] : []),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {title && <AlertTitle>{title}</AlertTitle>}
      {children}
    </Alert>
  );
}

export default Callout;
